/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Grid, Skeleton, Stack, TextField, Typography,
} from '@mui/material';
import { Place, Check, DeleteOutline, ZoomIn, ZoomOut, Public, Edit } from '@mui/icons-material';
import {
  boundsFromPolygon,
  clipLandToBounds,
  ensureGoogleOuterRing,
  isGlobeLikePolygon,
  landRingsForGoogle,
  loadWorldLand,
} from '../../utils/worldLand';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { clearMapOverlay, type MapOverlay } from '../../utils/googleMapPins';
import { searchLocations } from '../../services/locationService';
import { LAST_RESORT_MAP_CENTER, isValidMapCenter, peekCachedMapCenter, useMapCenter } from '../../utils/mapCenter';

export interface PolygonPoint {
  lat: number;
  lng: number;
}

interface ZonePolygonMapProps {
  polygon?: PolygonPoint[];
  landPolygons?: PolygonPoint[][];
  zones?: Array<{ id: string; name: string; polygon: PolygonPoint[]; color?: string; selected?: boolean }>;
  onPolygonChange?: (points: PolygonPoint[], landPolygons?: PolygonPoint[][]) => void;
  editable?: boolean;
  error?: string;
  center?: { lat: number; lng: number };
  height?: number | string;
}

type DrawMode = 'idle' | 'points' | 'edit';

type LatLngLike = { lat: () => number; lng: () => number };

type GMaps = {
  Map: new (el: HTMLElement, o: Record<string, unknown>) => GoogleMap;
  Polygon: new (o: Record<string, unknown>) => GooglePolygon;
  Polyline: new (o: Record<string, unknown>) => GooglePolyline;
  Circle: new (o: Record<string, unknown>) => MapOverlay;
  Marker: new (o: Record<string, unknown>) => GoogleMarker;
  SymbolPath: { CIRCLE: unknown };
  LatLngBounds: new () => GoogleBounds;
  event: {
    addListener: (t: unknown, e: string, fn: (...args: any[]) => void) => unknown;
    addDomListener: (t: unknown, e: string, fn: (...args: any[]) => void) => unknown;
    clearInstanceListeners: (t: unknown) => void;
    removeListener: (l: unknown) => void;
  };
};

type GoogleMap = {
  setCenter: (c: unknown) => void;
  setZoom: (z: number) => void;
  getZoom: () => number | undefined;
  getBounds: () => GoogleBounds | undefined;
  fitBounds: (b: unknown, o?: unknown) => void;
  setOptions: (o: Record<string, unknown>) => void;
  getDiv: () => HTMLElement;
};

type GooglePolygon = MapOverlay & {
  getPath: () => {
    getArray: () => LatLngLike[];
    getLength: () => number;
    addListener: (e: string, fn: () => void) => unknown;
  };
  setPaths: (p: unknown) => void;
  setEditable: (v: boolean) => void;
  setOptions?: (o: Record<string, unknown>) => void;
  setMap: (m: unknown | null) => void;
};

type GooglePolyline = MapOverlay & {
  setPath: (p: unknown) => void;
  setMap: (m: unknown | null) => void;
  setOptions?: (o: Record<string, unknown>) => void;
};

type GoogleMarker = MapOverlay & {
  setPosition: (p: unknown) => void;
  getPosition: () => LatLngLike | null | undefined;
  setMap: (m: unknown | null) => void;
  setVisible?: (v: boolean) => void;
};

type GoogleBounds = {
  extend: (p: unknown) => void;
  intersects: (b: GoogleBounds) => boolean;
  getNorthEast: () => LatLngLike;
  getSouthWest: () => LatLngLike;
  isEmpty?: () => boolean;
};

const DRAW_COLOR = '#1E90FF';
const EDITABLE_MAP_HEIGHT = 440;

function getMaps(): GMaps {
  return (window as unknown as { google: { maps: GMaps } }).google.maps;
}

function pathToPoints(poly: GooglePolygon): PolygonPoint[] {
  return poly.getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }));
}

function landRingsForCoords(coords: PolygonPoint[], saved?: PolygonPoint[][]): PolygonPoint[][] {
  const bounds = boundsFromPolygon(coords);
  if (!bounds) return [];

  // Always prefer a fresh land clip so we never keep a full-rectangle "shadow" fill.
  const fresh = landRingsForGoogle(clipLandToBounds(bounds));
  if (fresh.length) return fresh;

  if (saved?.length) {
    return saved.filter((ring) => ring.length >= 3 && !isRectangleShadowRing(ring, coords));
  }
  return [];
}

/** True when a ring is just the drawn bounding rectangle (the unwanted outer shadow). */
function isRectangleShadowRing(ring: PolygonPoint[], coords: PolygonPoint[]): boolean {
  if (ring.length < 4 || ring.length > 5 || coords.length < 3) return false;
  const a = boundsFromPolygon(ring);
  const b = boundsFromPolygon(coords);
  if (!a || !b) return false;
  const eps = 1e-4;
  return (
    Math.abs(a.north - b.north) < eps
    && Math.abs(a.south - b.south) < eps
    && Math.abs(a.east - b.east) < eps
    && Math.abs(a.west - b.west) < eps
  );
}

function destroyMapContainer(el: HTMLElement | null) {
  if (!el) return;
  try {
    el.replaceChildren();
  } catch {
    el.innerHTML = '';
  }
}

export default function ZonePolygonMap({
  polygon = [],
  landPolygons,
  zones = [],
  onPolygonChange,
  editable = false,
  error,
  center,
  height = 280,
}: ZonePolygonMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<GoogleMap | null>(null);
  const boundaryLineRef = useRef<GooglePolyline | null>(null);
  const editHandlesRef = useRef<GoogleMarker[]>([]);
  const landPolysRef = useRef<GooglePolygon[]>([]);
  const bgOverlaysRef = useRef<MapOverlay[]>([]);
  const draftOverlaysRef = useRef<MapOverlay[]>([]);
  const previewPolyRef = useRef<GooglePolygon | null>(null);
  const listenersRef = useRef<unknown[]>([]);
  const onPolygonChangeRef = useRef(onPolygonChange);
  const drawModeRef = useRef<DrawMode>('idle');
  const draftPointsRef = useRef<PolygonPoint[]>([]);
  const editNotifyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initGenRef = useRef(0);
  const boundaryCoordsRef = useRef<PolygonPoint[]>([]);
  const syncBoundaryFrameRef = useRef<(coords: PolygonPoint[], showHandles: boolean) => void>();

  const [pointCount, setPointCount] = useState(polygon.length);
  const [draftCount, setDraftCount] = useState(0);
  const [drawMode, setDrawMode] = useState<DrawMode>('idle');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [mapAlive, setMapAlive] = useState(false);
  const { ready, error: mapsError, hasKey } = useGoogleMaps();
  const preferredCenter = polygon.length >= 3 && !isGlobeLikePolygon(polygon)
    ? { lat: polygon[0].lat, lng: polygon[0].lng }
    : (isValidMapCenter(center) ? center : undefined);
  const { center: resolvedCenter } = useMapCenter(preferredCenter);
  const bootCenter = resolvedCenter
    || preferredCenter
    || peekCachedMapCenter()
    || LAST_RESORT_MAP_CENTER;
  const bootCenterRef = useRef(bootCenter);
  bootCenterRef.current = bootCenter;

  onPolygonChangeRef.current = onPolygonChange;
  drawModeRef.current = drawMode;
  const landPolygonsRef = useRef(landPolygons);
  landPolygonsRef.current = landPolygons;
  const polygonRef = useRef(polygon);
  polygonRef.current = polygon;

  const setMapCursor = useCallback((mode: DrawMode) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const drawingPoints = mode === 'points';
    map.setOptions({
      draggableCursor: drawingPoints ? 'crosshair' : null,
      draggable: true,
      gestureHandling: 'greedy',
    });
  }, []);

  const clearDraft = useCallback(() => {
    draftOverlaysRef.current.forEach((o) => clearMapOverlay(o));
    draftOverlaysRef.current = [];
    draftPointsRef.current = [];
    setDraftCount(0);
    if (previewPolyRef.current) {
      previewPolyRef.current.setMap(null);
      previewPolyRef.current = null;
    }
  }, []);

  const clearEditHandles = useCallback(() => {
    editHandlesRef.current.forEach((m) => {
      try { m.setMap(null); } catch { /* ignore */ }
    });
    editHandlesRef.current = [];
  }, []);

  const clearBoundaryFrame = useCallback(() => {
    clearEditHandles();
    if (boundaryLineRef.current) {
      boundaryLineRef.current.setMap(null);
      boundaryLineRef.current = null;
    }
    boundaryCoordsRef.current = [];
  }, [clearEditHandles]);

  const setMode = useCallback((mode: DrawMode) => {
    drawModeRef.current = mode;
    setDrawMode(mode);
    if (mode !== 'points') clearDraft();
    setMapCursor(mode);
    const showHandles = mode === 'edit';
    if (showHandles && boundaryCoordsRef.current.length >= 3 && editHandlesRef.current.length === 0) {
      syncBoundaryFrameRef.current?.(boundaryCoordsRef.current, true);
    } else {
      editHandlesRef.current.forEach((m) => m.setVisible?.(showHandles));
    }
  }, [clearDraft, setMapCursor]);

  const collectLandRings = useCallback((): PolygonPoint[][] => {
    return landPolysRef.current
      .map(pathToPoints)
      .filter((ring) => ring.length >= 3);
  }, []);

  const paintLandFill = useCallback((coords: PolygonPoint[], savedLand?: PolygonPoint[][]): PolygonPoint[][] => {
    const map = mapInstanceRef.current;
    if (!map) return [];
    const g = getMaps();
    landPolysRef.current.forEach((poly) => poly.setMap(null));
    landPolysRef.current = [];

    const globe = isGlobeLikePolygon(coords);
    const rings = landRingsForCoords(coords, savedLand)
      .filter((ring) => ring.length >= 3 && !isRectangleShadowRing(ring, coords))
      .map(ensureGoogleOuterRing);

    // Never paint the globe rectangle as fill — that floods oceans ("shadow").
    // World zones: land rings only. Local zones: land rings, or the drawn shape once.
    const toPaint = rings.length
      ? rings
      : (globe || coords.length < 3 ? [] : [ensureGoogleOuterRing(coords)]);

    toPaint.forEach((ring) => {
      if (ring.length < 3) return;
      // Globe/world: outline land only — Google often inverts fill on world-scale rings (ocean "shadow").
      const poly = new g.Polygon({
        map,
        paths: ring,
        strokeColor: DRAW_COLOR,
        strokeWeight: globe ? 1.25 : 2,
        strokeOpacity: 1,
        fillColor: DRAW_COLOR,
        fillOpacity: globe ? 0 : 0.35,
        clickable: false,
        editable: false,
        geodesic: false,
        zIndex: 2,
      });
      landPolysRef.current.push(poly);
    });
    return collectLandRings();
  }, [collectLandRings]);

  const notifyBoundary = useCallback((coords: PolygonPoint[]) => {
    const land = collectLandRings();
    setPointCount(coords.length);
    onPolygonChangeRef.current?.(coords, land);
  }, [collectLandRings]);

  const syncBoundaryFrame = useCallback((coords: PolygonPoint[], showHandles: boolean) => {
    const map = mapInstanceRef.current;
    if (!map || coords.length < 2) return;
    const g = getMaps();

    // World/globe rectangle must not be drawn — date-line bars + ocean shadow.
    if (isGlobeLikePolygon(coords)) {
      clearEditHandles();
      if (boundaryLineRef.current) {
        boundaryLineRef.current.setMap(null);
        boundaryLineRef.current = null;
      }
      boundaryCoordsRef.current = coords.slice();
      return;
    }

    const closed = [...coords, coords[0]];
    boundaryCoordsRef.current = coords.slice();

    if (boundaryLineRef.current) {
      boundaryLineRef.current.setPath(closed);
    } else {
      boundaryLineRef.current = new g.Polyline({
        map,
        path: closed,
        strokeColor: DRAW_COLOR,
        strokeWeight: 2,
        strokeOpacity: 1,
        clickable: false,
        zIndex: 4,
      });
    }

    clearEditHandles();
    if (!showHandles) return;

    coords.forEach((p, index) => {
      const marker = new g.Marker({
        map,
        position: p,
        draggable: true,
        flat: true,
        zIndex: 5,
        cursor: 'move',
        icon: {
          path: g.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#ffffff',
          fillOpacity: 1,
          strokeColor: DRAW_COLOR,
          strokeWeight: 2,
        },
      });
      g.event.addListener(marker, 'drag', () => {
        const pos = marker.getPosition();
        if (!pos) return;
        const next = boundaryCoordsRef.current.slice();
        next[index] = { lat: pos.lat(), lng: pos.lng() };
        boundaryCoordsRef.current = next;
        boundaryLineRef.current?.setPath([...next, next[0]]);
      });
      g.event.addListener(marker, 'dragend', () => {
        const next = boundaryCoordsRef.current.slice();
        if (next.length < 3) return;
        polygonRef.current = next;
        void loadWorldLand().then(() => {
          paintLandFill(next);
          notifyBoundary(next);
        }).catch(() => {
          paintLandFill(next);
          notifyBoundary(next);
        });
      });
      editHandlesRef.current.push(marker);
    });
  }, [clearEditHandles, paintLandFill, notifyBoundary]);
  syncBoundaryFrameRef.current = syncBoundaryFrame;

  /** Outline + handles only (Polyline) — cannot create an outer fill shadow. */
  const paintBoundary = useCallback((coords: PolygonPoint[], canEdit: boolean) => {
    if (coords.length < 3) return;
    syncBoundaryFrame(coords, canEdit);
  }, [syncBoundaryFrame]);

  const fitToBoundary = useCallback((coords: PolygonPoint[]) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const g = getMaps();
    if (isGlobeLikePolygon(coords)) {
      map.setCenter({ lat: 20, lng: 0 });
      map.setZoom(2);
      return;
    }
    const b = new g.LatLngBounds();
    coords.forEach((p) => b.extend(p));
    map.fitBounds(b, 36);
  }, []);

  const commitPolygon = useCallback((coords: PolygonPoint[]) => {
    const map = mapInstanceRef.current;
    if (!map || coords.length < 3) return;
    clearDraft();
    paintBoundary(coords, true);
    drawModeRef.current = 'edit';
    setDrawMode('edit');
    setMapCursor('edit');
    setPointCount(coords.length);
    fitToBoundary(coords);

    const applyFill = () => {
      const land = paintLandFill(coords);
      onPolygonChangeRef.current?.(coords, land);
    };
    void loadWorldLand().then(applyFill).catch(applyFill);
  }, [clearDraft, paintLandFill, paintBoundary, setMapCursor, fitToBoundary]);

  const removeZone = useCallback(() => {
    landPolysRef.current.forEach((poly) => poly.setMap(null));
    landPolysRef.current = [];
    clearBoundaryFrame();
    clearDraft();
    setPointCount(0);
    onPolygonChangeRef.current?.([], []);
    setMode('idle');
  }, [clearDraft, clearBoundaryFrame, setMode]);
  const finishOutline = useCallback(() => {
    if (draftPointsRef.current.length < 3) return;
    commitPolygon(draftPointsRef.current.slice());
  }, [commitPolygon]);

  const refreshDraftPreview = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const g = getMaps();
    draftOverlaysRef.current.forEach((o) => clearMapOverlay(o));
    draftOverlaysRef.current = [];
    const pts = draftPointsRef.current;
    if (!pts.length) return;

    // Draw line/fill first so a marker failure never hides the polygon outline.
    try {
      if (pts.length >= 2) {
        draftOverlaysRef.current.push(new g.Polyline({
          map,
          path: pts,
          strokeColor: DRAW_COLOR,
          strokeWeight: 3,
          strokeOpacity: 1,
          clickable: false,
          zIndex: 4,
        }));
      }
      if (pts.length >= 3) {
        draftOverlaysRef.current.push(new g.Polygon({
          map,
          paths: pts,
          strokeColor: DRAW_COLOR,
          fillColor: DRAW_COLOR,
          fillOpacity: 0.22,
          strokeWeight: 3,
          clickable: false,
          zIndex: 4,
        }));
      }
    } catch {
      /* keep going to place vertex markers */
    }

    pts.forEach((p) => {
      try {
        draftOverlaysRef.current.push(new g.Circle({
          map,
          center: p,
          radius: 28,
          fillColor: DRAW_COLOR,
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
          clickable: false,
          zIndex: 5,
        }));
      } catch {
        /* ignore single vertex failure */
      }
    });
  }, []);

  const addPolygonToMap = useCallback((coords: PolygonPoint[]) => {
    const map = mapInstanceRef.current;
    if (!map || coords.length < 3) return;
    paintLandFill(coords, landPolygonsRef.current);
    const canEdit = editable;
    const globe = isGlobeLikePolygon(coords);
    // World zone: keep idle so Edit shape is available (handles can't sit on a globe box).
    paintBoundary(coords, canEdit && !globe);
    if (canEdit && !globe) {
      drawModeRef.current = 'edit';
      setDrawMode('edit');
      setMapCursor('edit');
    } else {
      drawModeRef.current = 'idle';
      setDrawMode('idle');
      setMapCursor('idle');
    }
    setPointCount(coords.length);
    fitToBoundary(coords);
  }, [editable, paintLandFill, paintBoundary, setMapCursor, fitToBoundary]);

  const commitPolygonRef = useRef(commitPolygon);
  commitPolygonRef.current = commitPolygon;
  const addPolygonToMapRef = useRef(addPolygonToMap);
  addPolygonToMapRef.current = addPolygonToMap;
  const refreshDraftPreviewRef = useRef(refreshDraftPreview);
  refreshDraftPreviewRef.current = refreshDraftPreview;

  useEffect(() => {
    if (!ready || !mapRef.current) return;

    const el = mapRef.current;
    const g = getMaps();
    const gen = ++initGenRef.current;

    destroyMapContainer(el);
    mapInstanceRef.current = null;
    setMapAlive(false);

    const globe = polygonRef.current.length > 0 && isGlobeLikePolygon(polygonRef.current);
    const start = globe ? { lat: 20, lng: 0 } : bootCenterRef.current;

    let map: GoogleMap;
    try {
      // Raster map (no mapId) — reliable polygon stroke/fill without Advanced Marker requirements.
      map = new g.Map(el, {
        center: start,
        zoom: globe ? 2 : 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'greedy',
        minZoom: 1,
        clickableIcons: false,
        disableDoubleClickZoom: editable,
      });
    } catch (err) {
      setSetupError(err instanceof Error ? err.message : 'Zone map could not start');
      return;
    }

    if (gen !== initGenRef.current) {
      destroyMapContainer(el);
      return;
    }

    mapInstanceRef.current = map;
    setMapAlive(true);
    setSetupError(null);

    const listen = (target: unknown, event: string, fn: (...args: any[]) => void) => {
      const h = g.event.addListener(target, event, fn);
      listenersRef.current.push(h);
      return h;
    };

    if (editable) {
      try {
        listen(map, 'click', (e: { latLng?: LatLngLike }) => {
          if (drawModeRef.current !== 'points' || !e.latLng) return;
          draftPointsRef.current.push({ lat: e.latLng.lat(), lng: e.latLng.lng() });
          setDraftCount(draftPointsRef.current.length);
          refreshDraftPreviewRef.current();
        });

        listen(map, 'dblclick', (e: { latLng?: LatLngLike }) => {
          if (drawModeRef.current !== 'points') return;
          if (e?.latLng && draftPointsRef.current.length >= 3) {
            commitPolygonRef.current(draftPointsRef.current.slice());
          }
        });
      } catch (err) {
        setSetupError(err instanceof Error ? err.message : 'Zone draw tools failed to start');
      }
    }

    void loadWorldLand().then(() => {
      if (gen !== initGenRef.current || !mapInstanceRef.current) return;
      const coords = polygonRef.current;
      if (coords.length >= 3) addPolygonToMapRef.current(coords);
    }).catch(() => {
      if (gen !== initGenRef.current || !mapInstanceRef.current) return;
      const coords = polygonRef.current;
      if (coords.length >= 3) addPolygonToMapRef.current(coords);
    });

    return () => {
      initGenRef.current += 1;
      if (editNotifyTimer.current) clearTimeout(editNotifyTimer.current);
      listenersRef.current.forEach((h) => {
        try { g.event.removeListener(h); } catch { /* ignore */ }
      });
      listenersRef.current = [];
      landPolysRef.current.forEach((poly) => poly.setMap(null));
      landPolysRef.current = [];
      editHandlesRef.current.forEach((m) => { try { m.setMap(null); } catch { /* ignore */ } });
      editHandlesRef.current = [];
      if (boundaryLineRef.current) {
        boundaryLineRef.current.setMap(null);
        boundaryLineRef.current = null;
      }
      boundaryCoordsRef.current = [];
      if (previewPolyRef.current) {
        previewPolyRef.current.setMap(null);
        previewPolyRef.current = null;
      }
      bgOverlaysRef.current.forEach((o) => clearMapOverlay(o));
      bgOverlaysRef.current = [];
      draftOverlaysRef.current.forEach((o) => clearMapOverlay(o));
      draftOverlaysRef.current = [];
      mapInstanceRef.current = null;
      setMapAlive(false);
      destroyMapContainer(el);
    };
  }, [ready, editable]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !ready || !mapAlive) return;
    const g = getMaps();
    bgOverlaysRef.current.forEach((o) => clearMapOverlay(o));
    bgOverlaysRef.current = [];
    void loadWorldLand().then(() => {
      if (!mapInstanceRef.current) return;
      zones.forEach((z) => {
        if (z.polygon.length < 3) return;
        if (z.selected && polygon.length >= 3) return;
        const bounds = boundsFromPolygon(z.polygon);
        if (!bounds) return;
        const globe = isGlobeLikePolygon(z.polygon);

        // Do not stroke/fill the world rectangle — it paints ocean "shadow" + date-line bars.
        if (!globe) {
          bgOverlaysRef.current.push(new g.Polygon({
            map: mapInstanceRef.current,
            paths: ensureGoogleOuterRing(z.polygon),
            strokeColor: z.color || '#2563eb',
            fillOpacity: 0,
            strokeWeight: z.selected ? 3 : 2,
            strokeOpacity: 1,
            clickable: false,
            geodesic: false,
          }));
        }

        landRingsForGoogle(clipLandToBounds(bounds)).forEach((ring) => {
          if (ring.length < 3) return;
          bgOverlaysRef.current.push(new g.Polygon({
            map: mapInstanceRef.current,
            paths: ring,
            strokeColor: z.color || '#2563eb',
            fillColor: z.color || '#2563eb',
            // World zone: stroke only — fill inverts to oceans on globe-scale rings.
            fillOpacity: globe ? 0 : (z.selected ? 0.28 : 0.12),
            strokeWeight: globe ? 1.1 : 0.8,
            strokeOpacity: 1,
            clickable: false,
            geodesic: false,
          }));
        });
      });
    });
  }, [zones, polygon.length, ready, mapAlive]);

  useEffect(() => {
    if (
      polygon.length >= 3
      && mapInstanceRef.current
      && mapAlive
      && !boundaryLineRef.current
      && landPolysRef.current.length === 0
    ) {
      addPolygonToMap(polygon);
    }
  }, [polygon, addPolygonToMap, mapAlive]);

  // Keep map center in sync once GPS/cache resolves (create flow).
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapAlive || polygonRef.current.length >= 3) return;
    if (!isValidMapCenter(resolvedCenter)) return;
    map.setCenter(resolvedCenter);
  }, [resolvedCenter, mapAlive]);

  const handleSearch = async () => {
    const q = searchQuery.trim();
    const map = mapInstanceRef.current;
    if (!q || !map) return;
    setSearching(true);
    try {
      const rows = await searchLocations(q);
      if (rows[0]) {
        map.setCenter({ lat: rows[0].lat, lng: rows[0].lng });
        map.setZoom(14);
      }
    } finally {
      setSearching(false);
    }
  };

  const mapHeight = editable ? EDITABLE_MAP_HEIGHT : height;

  if (!hasKey) {
    return <Alert severity="warning">Google Maps key missing. Set VITE_GOOGLE_MAPS_API_KEY in frontend/.env.local</Alert>;
  }
  if (mapsError) return <Alert severity="error">{mapsError}</Alert>;
  if (setupError) return <Alert severity="error">{setupError}</Alert>;

  return (
    <Box data-map-engine="google-maps">
      {editable && (
        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                size="small"
                label="Search location"
                placeholder="City or area name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Button fullWidth variant="outlined" onClick={handleSearch} disabled={searching || !mapAlive} sx={{ height: 40 }}>
                {searching ? 'Searching…' : 'Go to location'}
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button
              size="small"
              startIcon={<Place />}
              variant={drawMode === 'points' ? 'contained' : 'outlined'}
              disabled={!mapAlive}
              onClick={() => setMode(drawMode === 'points' ? 'idle' : 'points')}
              color={drawMode === 'points' ? 'primary' : 'inherit'}
            >
              {drawMode === 'points' ? `Clicking (${draftCount} pts)` : 'Click points'}
            </Button>
            {drawMode === 'points' && draftCount >= 3 && (
              <Button size="small" startIcon={<Check />} variant="contained" color="success" onClick={finishOutline}>
                Done
              </Button>
            )}
            {(pointCount >= 3 || polygon.length >= 3) && (
              <Button
                size="small"
                startIcon={<Edit />}
                variant={drawMode === 'edit' || (drawMode === 'points' && isGlobeLikePolygon(polygon.length >= 3 ? polygon : boundaryCoordsRef.current)) ? 'contained' : 'outlined'}
                color={drawMode === 'edit' || drawMode === 'points' ? 'secondary' : 'inherit'}
                disabled={!mapAlive}
                onClick={() => {
                  const current = boundaryCoordsRef.current.length >= 3
                    ? boundaryCoordsRef.current
                    : (polygon.length >= 3 ? polygon : polygonRef.current);
                  // World zone has no drag handles — Edit starts a click-points redraw.
                  if (isGlobeLikePolygon(current)) {
                    if (drawMode === 'points') {
                      setMode('idle');
                      return;
                    }
                    clearDraft();
                    setMode('points');
                    return;
                  }
                  setMode(drawMode === 'edit' ? 'idle' : 'edit');
                }}
              >
                {drawMode === 'edit'
                  ? 'Editing…'
                  : (drawMode === 'points' && isGlobeLikePolygon(polygon.length >= 3 ? polygon : boundaryCoordsRef.current)
                    ? 'Redrawing…'
                    : 'Edit shape')}
              </Button>
            )}
            {(pointCount > 0 || draftCount > 0 || polygon.length >= 3) && (
              <Button size="small" startIcon={<DeleteOutline />} color="error" onClick={removeZone}>
                Clear
              </Button>
            )}
            <Button
              size="small"
              startIcon={<ZoomOut />}
              variant="outlined"
              disabled={!mapAlive}
              onClick={() => {
                const map = mapInstanceRef.current;
                if (!map) return;
                map.setZoom(Math.max(1, (map.getZoom() ?? 12) - 1));
              }}
            >
              Zoom out
            </Button>
            <Button
              size="small"
              startIcon={<ZoomIn />}
              variant="outlined"
              disabled={!mapAlive}
              onClick={() => {
                const map = mapInstanceRef.current;
                if (!map) return;
                map.setZoom((map.getZoom() ?? 12) + 1);
              }}
            >
              Zoom in
            </Button>
            <Button
              size="small"
              startIcon={<Public />}
              variant="outlined"
              disabled={!mapAlive}
              onClick={() => {
                const map = mapInstanceRef.current;
                if (!map) return;
                map.setCenter({ lat: 20, lng: 0 });
                map.setZoom(2);
              }}
            >
              Full map
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {drawMode === 'points' && (
              <Chip
                size="small"
                color="primary"
                label={
                  isGlobeLikePolygon(polygon.length >= 3 ? polygon : boundaryCoordsRef.current)
                    ? (draftCount < 3
                      ? 'World zone: click 3+ points to redraw a new boundary, then Done'
                      : 'Tap Done to replace the world boundary')
                    : (draftCount < 3
                      ? 'Click the map to add points, then tap Done (or double-click to close)'
                      : 'Double-click or tap Done to finish')
                }
              />
            )}
            {drawMode === 'edit' && (
              <Chip
                size="small"
                color="secondary"
                label="Drag white corner handles to reshape — blue fill is the zone only"
              />
            )}
            {drawMode === 'idle' && (pointCount > 0 || polygon.length >= 3) && (
              <Chip
                size="small"
                color="success"
                label={isGlobeLikePolygon(polygon.length >= 3 ? polygon : boundaryCoordsRef.current)
                  ? 'World zone · tap Edit shape to redraw with click points'
                  : `${Math.max(pointCount, polygon.length)} points · tap Edit shape for handles`}
              />
            )}
            {drawMode === 'idle' && !pointCount && polygon.length < 3 && (
              <Typography variant="caption" color="text.secondary">
                Search your city, then Click points to draw the zone boundary.
              </Typography>
            )}
          </Stack>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
      )}

      <Box sx={{ position: 'relative', width: '100%' }}>
        {!ready && (
          <Skeleton
            variant="rounded"
            height={mapHeight}
            sx={{ borderRadius: 2, position: 'absolute', inset: 0, zIndex: 2 }}
          />
        )}
        <Box
          ref={mapRef}
          sx={{
            width: '100%',
            height: mapHeight,
            borderRadius: 2,
            border: 1,
            borderColor: error ? 'error.main' : 'divider',
            overflow: 'hidden',
            bgcolor: 'action.hover',
          }}
        />
      </Box>
    </Box>
  );
}
